// Card-typeline predicates. Pure. No React, no Three.js.
//
// ── Codex (Artifact) ──
//   "Artifacts have two special subtypes (Automaton, Monument) that
//    do have inherent game rules."
//   Automaton. "Automatons are artifacts that are minions." They
//     enter the realm as units; they are never carriable and never
//     get the Field-vs-Equip choice.
//   Monument. "an artifact subtype that cannot be carried." It is
//     conjured atop a site.
//   Carriable. "any artifact that's not a monument and not a minion
//     (i.e. not an automaton)."
//
// Catalog cards come in two shapes: normalized cards carry `traits`
// (string array, see normalizeCards.js), raw/test fixtures carry
// `subTypes` (string or array). Every subtype read here accepts both
// This was the root cause of Kairos getting a bogus equip prompt:
// the runtime tested `fullCard.subTypes`, which is undefined on
// normalized cards, so the Automaton branch never fired.

export function cardSubtypesLower(fullCard) {
  const raw = fullCard?.traits ?? fullCard?.subTypes;
  return (Array.isArray(raw) ? raw : [raw])
    .filter(Boolean)
    .flatMap((s) => String(s).toLowerCase().split(/[,;]+/))
    .map((s) => s.trim())
    .filter(Boolean);
}

function isArtifactType(fullCard) {
  return (fullCard?.type ?? fullCard?.guardian?.type) === 'Artifact';
}

/** Artifact minion (Crave Golem, Kairos the Archivist). Plays as a unit. */
export function isAutomatonCard(fullCard) {
  return isArtifactType(fullCard) && cardSubtypesLower(fullCard).includes('automaton');
}

/** Uncarriable artifact conjured atop a site (Shrine of the Dragonlord). */
export function isMonumentCard(fullCard) {
  return isArtifactType(fullCard) && cardSubtypesLower(fullCard).includes('monument');
}

/** Codex Carriable Artifact: not a Monument, not an Automaton. */
export function isCarriableArtifactCard(fullCard) {
  return isArtifactType(fullCard)
    && !isAutomatonCard(fullCard)
    && !isMonumentCard(fullCard);
}

/**
 * "Is this a minion?" for rules purposes: printed Minions PLUS
 * Automatons (codex: "Automatons are artifacts that are minions";
 * "Cards with Multiple Types." They are both types at once). Every
 * minion rule (combat stats, death at lethal damage, summoning
 * sickness, defend/intercept, movement, automation filters) must go
 * through this instead of comparing `type` directly, because the
 * `type` field carries the printed primary type. 'Artifact' for
 * every automaton. `type === 'Artifact'` checks stay true for
 * automatons on purpose.
 */
export function countsAsMinion(card) {
  if (!card) return false;
  if ((card.type ?? card.guardian?.type) === 'Minion') return true;
  // Enchantress: the animated aura is "an aura minion." A minion IN ADDITION
  // to its other types (codex "Cards with Multiple Types"). Its printed `type`
  // stays 'Aura' so aura-type-gated paths keep it; combat / death / summoning
  // rules see it through this predicate for the animate window.
  if (card.enchantressAnimated) return true;
  return isAutomatonCard(card);
}

/** "A unit is a minion or an avatar" (codex Unit). */
export function countsAsUnit(card) {
  return countsAsMinion(card) || (card?.type ?? card?.guardian?.type) === 'Avatar';
}
